import { Controller } from '@hotwired/stimulus'
import {
  DEFAULT_BLOCK_ID,
  UNICODE_BLOCKS,
  codepointsForBlock,
} from '../lib/unicode_blocks.js'
import {
  categoryNeedsUnicodeNamesForSearch,
  codepointMatchesSearch,
  ensureUnicodeNames,
  getCharacterMeta,
  getRelatedCodepoints,
  isCuratedCodepoint,
} from '../lib/character_correlations.js'
import {
  DEFAULT_TEXT_COLOR,
  MAX_CUSTOM_TEXT_COLORS,
  SHEETS_STANDARD_COLORS,
  SHEETS_THEME_GRID,
  SHEETS_THEME_LABELS,
  TEXT_COLOR_STORAGE_KEY,
  isLightColor,
  loadCustomTextColors,
  normalizeHex,
  saveCustomTextColors,
} from '../lib/sheets_text_colors.js'
import {
  DEFAULT_GLYPH_SIZE,
  GLYPH_SIZES,
  GLYPH_SIZE_TOOLTIP_NAMES,
  loadGlyphSize,
  normalizeGlyphSize,
  saveGlyphSize,
  stepGlyphSize,
} from '../lib/char_glyph_sizes.js'
import { trackFormulaCopy } from '../lib/playground_analytics.js'
import {
  buildCategoryBadgesHtml,
  buildCodepointFieldsHtml,
} from '../lib/char_detail_fields.js'

export default class extends Controller {
  static targets = [
    'grid',
    'charMain',
    'detailSidebar',
    'detailSidebarToggle',
    'detailPanel',
    'detailEmpty',
    'detailHeaderGlyph',
    'detailContent',
    'sheetArtMatch',
    'similarSection',
    'textColorPopover',
    'textColorPalette',
    'textColorBar',
    'textColorCustom',
    'randomTen',
    'toast',
    'categoryChips',
    'glyphSizeDownButton',
    'glyphSizeUpButton',
    'nameSearchInput',
    'nameSearchWrap',
    'nameSearchToggle',
    'nameSearchPanel',
  ]

  static values = {
    selectedCodepoint: Number,
    sheetArtExamples: Array,
  }

  connect() {
    this.activeBlockId = DEFAULT_BLOCK_ID
    this.selectedCp = null
    this.textColorOpen = false
    this.textColor = DEFAULT_TEXT_COLOR
    this.glyphSize = DEFAULT_GLYPH_SIZE
    this.customTextColors = loadCustomTextColors()
    this.customTextColorsEditing = false
    this.nameSearchDebounceTimeout = null
    this.nameSearchQuery = ''
    this.nameSearchOpen = false
    this.nameSearchNamesPromise = null
    this.detailSidebarOpen = false

    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    this.boundBeforeCache = this.handleBeforeCache.bind(this)
    this.boundCategoryBadgeClick = this.handleCategoryBadgeClick.bind(this)
    document.addEventListener('click', this.boundDocumentClick)
    document.addEventListener('keydown', this.boundDocumentKeydown = this.handleDocumentKeydown.bind(this))
    document.addEventListener('turbo:before-cache', this.boundBeforeCache)
    this.element.addEventListener('click', this.boundCategoryBadgeClick)

    this.renderTextColorPicker()

    const savedColor = localStorage.getItem(TEXT_COLOR_STORAGE_KEY)
    if (savedColor) this.applyTextColor(savedColor, { persist: false })

    this.applyGlyphSize(loadGlyphSize(), { persist: false })

    this.renderCategoryChips()
    this.renderGrid()
    this.pickRandomTen()
    this.syncStickyOffsets()

    this.boundResizeStickyOffsets = this.syncStickyOffsets.bind(this)
    window.addEventListener('resize', this.boundResizeStickyOffsets)
    if (typeof ResizeObserver !== 'undefined') {
      const toolbar = this.element.querySelector('#char-toolbar')
      if (toolbar) {
        this.toolbarResizeObserver = new ResizeObserver(this.boundResizeStickyOffsets)
        this.toolbarResizeObserver.observe(toolbar)
      }
    }

    if (this.hasSelectedCodepointValue && this.selectedCodepointValue) {
      this.selectGlyph(this.selectedCodepointValue, { skipUrl: true })
      this.openDetailSidebar()
    }
  }

  disconnect() {
    document.removeEventListener('click', this.boundDocumentClick)
    document.removeEventListener('keydown', this.boundDocumentKeydown)
    document.removeEventListener('turbo:before-cache', this.boundBeforeCache)
    this.element.removeEventListener('click', this.boundCategoryBadgeClick)
    window.removeEventListener('resize', this.boundResizeStickyOffsets)
    this.toolbarResizeObserver?.disconnect()
    clearTimeout(this.nameSearchDebounceTimeout)
  }

  handleBeforeCache() {
    this.renderCategoryChips()
  }

  selectCategory(event) {
    event?.preventDefault?.()
    const blockId = event.currentTarget.dataset.blockId
    if (!blockId) return

    const hadSearch = Boolean(this.nameSearchQuery)
    if (hadSearch) {
      this.nameSearchQuery = ''
      if (this.hasNameSearchInputTarget) this.nameSearchInputTarget.value = ''
      this.updateNameSearchUI()
    }

    const blockChanged = blockId !== this.activeBlockId
    if (blockChanged) {
      this.activeBlockId = blockId
      this.updateCategoryChips()
    }

    if (blockChanged || hadSearch) {
      this.renderGrid()
      if (this.isWallOpen()) this.wallController()?.renderGrid()
    }

    if (this.isWallOpen()) {
      this.wallController()?.close()
    }

    this.scrollToGridTop()
  }

  selectGlyphFromEvent(event) {
    event.preventDefault()
    const cp = parseInt(event.currentTarget.dataset.codepoint, 10)
    if (Number.isNaN(cp)) return
    this.selectGlyph(cp)
  }

  selectGlyph(codepoint, { skipUrl = false } = {}) {
    if (!this.validCodepoint(codepoint)) return

    this.selectedCp = codepoint
    this.openDetailSidebar()
    this.renderDetailPanel()
    this.loadUnicodeNameIfNeeded(codepoint)
    this.highlightSelectedCell()
    this.scrollToSelectedCell()

    if (this.hasDetailPanelTarget) {
      this.detailPanelTarget.scrollTop = 0
    }

    if (!skipUrl) {
      const hex = codepoint.toString(16)
      window.history.pushState({}, '', `/char?u=${hex}`)
    }
  }

  clearSelection() {
    this.selectedCp = null
    this.closeDetailSidebar()
    if (this.hasNameSearchInputTarget) {
      this.nameSearchInputTarget.value = ''
      this.nameSearchQuery = ''
      this.renderGrid()
      this.updateNameSearchUI()
    }
    this.renderDetailPanel()
    this.highlightSelectedCell()
    window.history.pushState({}, '', '/char')
    requestAnimationFrame(() => this.nameSearchInputTarget?.focus())
  }

  toggleDetailSidebar(event) {
    event?.stopPropagation()
    if (!this.selectedCp) return

    if (this.detailSidebarOpen) {
      this.closeDetailSidebar()
    } else {
      this.openDetailSidebar()
    }
  }

  openDetailSidebar() {
    if (!this.selectedCp) return
    this.detailSidebarOpen = true
    this.updateDetailSidebarUI()
  }

  closeDetailSidebar() {
    this.detailSidebarOpen = false
    this.updateDetailSidebarUI()
  }

  updateDetailSidebarUI() {
    const show = this.detailSidebarOpen && this.selectedCp

    if (this.hasDetailSidebarTarget) {
      this.detailSidebarTarget.classList.toggle('hidden', !show)
    }

    if (this.hasCharMainTarget) {
      this.charMainTarget.classList.toggle('char-main--sidebar-open', show)
    }

    if (this.hasDetailSidebarToggleTarget) {
      this.detailSidebarToggleTarget.classList.toggle('hidden', !this.selectedCp)
      this.detailSidebarToggleTarget.setAttribute('aria-expanded', String(show))
      this.detailSidebarToggleTarget.classList.toggle('bg-slate-100', show)
    }

    this.syncStickyOffsets()
  }

  submitUnifiedSearch(event) {
    event?.preventDefault()
    clearTimeout(this.nameSearchDebounceTimeout)
    this.applyUnifiedSearch()
  }

  nameSearchInputChanged() {
    clearTimeout(this.nameSearchDebounceTimeout)
    this.nameSearchDebounceTimeout = setTimeout(() => this.applyUnifiedSearch(), 200)
  }

  toggleNameSearch(event) {
    event?.stopPropagation()
    if (this.nameSearchOpen) {
      this.closeNameSearchPanel({ focusToggle: true })
    } else {
      this.openNameSearch()
    }
  }

  openNameSearch() {
    this.nameSearchOpen = true
    this.updateNameSearchUI()
    requestAnimationFrame(() => this.nameSearchInputTarget?.focus())
  }

  closeNameSearch(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.closeNameSearchPanel()
  }

  closeNameSearchPanel({ focusToggle = false } = {}) {
    this.nameSearchOpen = false
    this.updateNameSearchUI()
    if (focusToggle && this.hasNameSearchToggleTarget) {
      this.nameSearchToggleTarget.focus()
    }
  }

  updateNameSearchUI() {
    if (this.hasNameSearchPanelTarget) {
      this.nameSearchPanelTarget.classList.toggle('hidden', !this.nameSearchOpen)
      this.nameSearchPanelTarget.classList.toggle('flex', this.nameSearchOpen)
    }

    if (this.hasNameSearchToggleTarget) {
      this.nameSearchToggleTarget.setAttribute('aria-expanded', String(this.nameSearchOpen))
      const hasQuery = Boolean(this.nameSearchQuery)
      this.nameSearchToggleTarget.classList.toggle('bg-slate-100', this.nameSearchOpen || hasQuery)
    }

    this.syncStickyOffsets()
  }

  applyUnifiedSearch() {
    if (!this.hasNameSearchInputTarget) return

    const query = this.nameSearchInputTarget.value.trim()

    if (!query) {
      this.nameSearchQuery = ''
      this.clearInputError(this.nameSearchInputTarget)
      this.updateNameSearchUI()
      if (this.isWallOpen()) {
        this.wallController()?.syncSearch('')
      } else {
        this.renderGrid()
      }
      return
    }

    if (/^\d+$/.test(query)) {
      const n = parseInt(query, 10)
      if (!this.validCodepoint(n)) {
        this.flashInputError(this.nameSearchInputTarget)
        return
      }

      this.clearInputError(this.nameSearchInputTarget)
      this.nameSearchQuery = ''
      this.updateNameSearchUI()

      if (this.isWallOpen()) {
        this.wallController()?.searchByCode(n)
        return
      }

      this.renderGrid()
      window.history.pushState({}, '', `/char?n=${n}`)
      this.selectGlyph(n, { skipUrl: true })
      return
    }

    this.clearInputError(this.nameSearchInputTarget)
    this.nameSearchQuery = query
    this.ensureNameSearchNamesLoaded()
    this.updateNameSearchUI()

    if (this.isWallOpen()) {
      this.wallController()?.syncSearch(this.nameSearchQuery)
    } else {
      this.renderGrid()
    }
  }

  applyNameSearch() {
    this.applyUnifiedSearch()
  }

  gridBlockForSearch() {
    if (this.nameSearchQuery) return { id: 'all' }
    return UNICODE_BLOCKS.find((b) => b.id === this.activeBlockId) || UNICODE_BLOCKS[0]
  }

  ensureNameSearchNamesLoaded() {
    const codepoints = codepointsForBlock(this.gridBlockForSearch())
    if (!categoryNeedsUnicodeNamesForSearch(codepoints)) return

    if (!this.nameSearchNamesPromise) {
      this.nameSearchNamesPromise = ensureUnicodeNames().then(() => {
        if (this.nameSearchQuery) this.renderGrid()
      })
    }
  }

  sizeDown(event) {
    event?.stopPropagation()
    this.applyGlyphSize(stepGlyphSize(this.glyphSize, -1))
  }

  sizeUp(event) {
    event?.stopPropagation()
    this.applyGlyphSize(stepGlyphSize(this.glyphSize, 1))
  }

  applyGlyphSize(size, { persist = true } = {}) {
    this.glyphSize = persist ? saveGlyphSize(size) : normalizeGlyphSize(size)
    this.element.dataset.glyphSize = this.glyphSize
    this.updateGlyphSizeButtons()
    this.wallController()?.syncGlyphSize(this.glyphSize)
  }

  updateGlyphSizeButtons() {
    const atMin = this.glyphSize === GLYPH_SIZES[0]
    const atMax = this.glyphSize === GLYPH_SIZES[GLYPH_SIZES.length - 1]
    const tooltipName = GLYPH_SIZE_TOOLTIP_NAMES[this.glyphSize] || this.glyphSize

    if (this.hasGlyphSizeDownButtonTarget) {
      this.glyphSizeDownButtonTarget.disabled = atMin
      this.glyphSizeDownButtonTarget.title = atMin
        ? 'Smallest grid size'
        : `Smaller (current: ${tooltipName})`
    }

    if (this.hasGlyphSizeUpButtonTarget) {
      this.glyphSizeUpButtonTarget.disabled = atMax
      this.glyphSizeUpButtonTarget.title = atMax
        ? 'Largest grid size'
        : `Bigger (current: ${tooltipName})`
    }
  }

  openTextColor(event) {
    event?.stopPropagation()
    this.textColorOpen = !this.textColorOpen
    this.customTextColorsEditing = false
    this.togglePopover(this.textColorPopoverTarget, this.textColorOpen)
    if (this.textColorOpen) {
      this.renderTextColorPicker()
      this.updateTextColorSelection()
    }
  }

  pickTextColor(event) {
    const hex = normalizeHex(event.currentTarget.dataset.color)
    if (!hex) return

    if (this.customTextColorsEditing && event.currentTarget.dataset.customColor === 'true') {
      this.removeCustomTextColor(hex)
      return
    }

    this.applyTextColor(hex)
    this.closeTextColorPopover()
  }

  resetTextColor(event) {
    event?.preventDefault()
    this.applyTextColor(DEFAULT_TEXT_COLOR)
    this.closeTextColorPopover()
  }

  openCustomTextColorPicker(event) {
    event?.preventDefault()
    if (this.customTextColors.length >= MAX_CUSTOM_TEXT_COLORS) return
    this.textColorCustomTarget?.click()
  }

  toggleCustomTextColorEdit(event) {
    event?.preventDefault()
    this.customTextColorsEditing = !this.customTextColorsEditing
    this.renderCustomTextColors()
  }

  customTextColor(event) {
    const hex = normalizeHex(event.currentTarget.value)
    if (!hex) return

    if (!this.customTextColors.includes(hex)) {
      this.customTextColors = saveCustomTextColors([hex, ...this.customTextColors])
      this.renderCustomTextColors()
    }

    this.applyTextColor(hex)
    event.currentTarget.value = hex
  }

  shuffleRandomTen() {
    this.pickRandomTen()
  }

  async copyFromEvent(event) {
    event.preventDefault()
    event.stopPropagation()
    const formula = event.currentTarget.dataset.formula
    if (!formula) return
    await this.copyFormula(formula, event.currentTarget)
  }

  async copyCardFromEvent(event) {
    if (event.target.closest('a')) return

    const formula = event.currentTarget.dataset.formula
    if (!formula) return
    await this.copyFormula(formula, event.currentTarget)
  }

  async copyCardFromKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()

    const formula = event.currentTarget.dataset.formula
    if (!formula) return
    await this.copyFormula(formula, event.currentTarget)
  }

  async copyCharacterFromEvent(event) {
    event.preventDefault()
    event.stopPropagation()
    const cp = parseInt(event.currentTarget.dataset.codepoint, 10)
    if (!this.validCodepoint(cp)) return
    await this.copyCharacter(cp, event.currentTarget)
  }

  handleCategoryBadgeClick(event) {
    const badge = event.target.closest('[data-characters-category-badge]')
    if (!badge || !this.element.contains(badge)) return

    event.preventDefault()
    this.selectCategory({ currentTarget: badge, preventDefault: () => {} })
  }

  handleDocumentClick(event) {
    if (this.textColorOpen && this.hasTextColorPopoverTarget && !this.textColorPopoverTarget.contains(event.target)) {
      const trigger = this.element.querySelector('[data-characters-text-color-trigger]')
      if (!trigger?.contains(event.target)) this.closeTextColorPopover()
    }

    if (
      this.nameSearchOpen &&
      this.hasNameSearchWrapTarget &&
      !this.nameSearchWrapTarget.contains(event.target)
    ) {
      this.closeNameSearchPanel()
    }
  }

  handleDocumentKeydown(event) {
    if (event.key !== 'Escape') return

    if (this.textColorOpen) {
      this.closeTextColorPopover()
      return
    }

    if (this.nameSearchOpen) this.closeNameSearchPanel()
  }

  categoryChipsContainer() {
    if (this.hasCategoryChipsTarget) return this.categoryChipsTarget

    // Turbo can serve cached HTML from before chips were JS-rendered.
    return this.element.querySelector('#char-toolbar [data-characters-target="categoryChips"], #char-toolbar .ml-auto')
  }

  renderCategoryChips() {
    const container = this.categoryChipsContainer()
    if (!container) return

    const chips = container.querySelectorAll('[data-block-id]')
    const chipsMissingIcon =
      chips.length !== UNICODE_BLOCKS.length ||
      [...chips].some((chip) => !chip.querySelector('.char-category-icon')?.textContent?.trim())

    if (chipsMissingIcon) {
      const fragment = document.createDocumentFragment()
      UNICODE_BLOCKS.forEach((block) => {
        fragment.appendChild(this.buildCategoryChip(block))
      })
      container.replaceChildren(fragment)
    }

    this.updateCategoryChips()
    this.syncStickyOffsets()
  }

  buildCategoryChip(block) {
    const button = document.createElement('button')
    button.type = 'button'
    button.dataset.blockId = block.id
    button.dataset.action = 'click->characters#selectCategory'
    button.className =
      'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50'
    button.setAttribute('aria-label', block.label)
    button.setAttribute('aria-pressed', 'false')
    button.title = block.label

    const icon = document.createElement('span')
    icon.className = 'char-category-icon text-base leading-none shrink-0 select-none'
    icon.setAttribute('aria-hidden', 'true')
    icon.textContent = block.icon ?? ''

    const label = document.createElement('span')
    label.className = 'char-category-label'
    label.textContent = block.label

    button.appendChild(icon)
    button.appendChild(label)
    return button
  }

  updateCategoryChips() {
    const container = this.categoryChipsContainer()
    if (!container) return

    container.querySelectorAll('[data-block-id]').forEach((chip) => {
      const active = chip.dataset.blockId === this.activeBlockId
      chip.classList.toggle('font-semibold', active)
      chip.classList.toggle('bg-slate-100', active)
      chip.setAttribute('aria-pressed', String(active))
    })
  }

  syncStickyOffsets() {
    const toolbar = this.element.querySelector('#char-toolbar')
    if (!toolbar) return

    const height = Math.ceil(toolbar.getBoundingClientRect().height)
    this.element.style.setProperty('--char-detail-sticky-top', `${height}px`)
    this.wallController()?.syncToolbarOffset()
  }

  wallController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'char-wall')
  }

  isWallOpen() {
    return Boolean(this.wallController()?.isOpen)
  }

  renderGrid() {
    if (!this.hasGridTarget) return

    let codepoints = codepointsForBlock(this.gridBlockForSearch())

    const queryLower = this.nameSearchQuery.toLowerCase()
    if (queryLower) {
      codepoints = codepoints.filter((cp) => codepointMatchesSearch(cp, queryLower))
    }

    const fragment = document.createDocumentFragment()
    if (!codepoints.length && queryLower) {
      const message = document.createElement('p')
      message.className = 'col-span-full py-12 text-center text-sm text-slate-500'
      message.textContent = `No characters match "${this.nameSearchQuery}". Try a decimal code like 9733 for ★, or clear the search.`
      fragment.appendChild(message)
    } else {
      codepoints.forEach((cp) => {
        fragment.appendChild(this.buildGlyphCell(cp))
      })
    }

    this.gridTarget.replaceChildren(fragment)
    this.highlightSelectedCell()
  }

  buildGlyphCell(codepoint) {
    const button = document.createElement('button')
    button.type = 'button'
    button.dataset.codepoint = String(codepoint)
    button.dataset.action = 'click->characters#selectGlyphFromEvent'
    button.title = this.glyphLabel(codepoint)
    button.setAttribute('aria-label', `${this.glyphLabel(codepoint)}, ${this.charFormula(codepoint)}`)
    button.className =
      'char-glyph-cell char-glyph flex items-center justify-center aspect-square rounded-lg bg-transparent border-0 cursor-pointer transition-[transform,box-shadow,background-color] duration-150 ease-out hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50'
    button.setAttribute('aria-pressed', 'false')

    const span = document.createElement('span')
    span.className = 'char-glyph pointer-events-none select-none leading-none'
    span.textContent = this.glyphFor(codepoint)
    button.appendChild(span)

    return button
  }

  renderDetailPanel() {
    if (!this.hasDetailEmptyTarget || !this.hasDetailContentTarget) return

    if (!this.selectedCp) {
      this.detailEmptyTarget.classList.remove('hidden')
      this.detailContentTarget.classList.add('hidden')
      if (this.hasDetailHeaderGlyphTarget) this.detailHeaderGlyphTarget.replaceChildren()
      if (this.hasSheetArtMatchTarget) this.sheetArtMatchTarget.replaceChildren()
      if (this.hasSimilarSectionTarget) this.similarSectionTarget.replaceChildren()
      return
    }

    this.detailEmptyTarget.classList.add('hidden')
    this.detailContentTarget.classList.remove('hidden')

    const cp = this.selectedCp
    const meta = getCharacterMeta(cp)
    const glyph = this.glyphFor(cp)
    const charFormula = this.charFormula(cp)
    const tags = (meta.tags || []).slice(0, 6)

    if (this.hasDetailHeaderGlyphTarget) {
      const glyphButton = document.createElement('button')
      glyphButton.type = 'button'
      glyphButton.className =
        'char-glyph char-glyph-detail-header leading-none inline-flex items-start p-0 rounded-lg border-0 bg-transparent cursor-pointer transition-[transform,background-color] duration-150 ease-out hover:bg-slate-50 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50'
      glyphButton.dataset.codepoint = String(cp)
      glyphButton.dataset.copyGlyph = 'true'
      glyphButton.dataset.action = 'click->characters#copyCharacterFromEvent'
      glyphButton.setAttribute('aria-label', `Copy character: ${meta.name}`)
      glyphButton.title = `${meta.name}. Click to copy the character.`
      glyphButton.textContent = glyph
      this.detailHeaderGlyphTarget.replaceChildren(glyphButton)
    }

    this.detailContentTarget.innerHTML = `
      <div class="space-y-4">
        <p class="text-sm font-medium text-slate-900">${this.escapeHtml(meta.name)}</p>

        ${tags.length ? `
          <div class="flex flex-wrap gap-1.5">
            ${tags.map((tag) => `<span class="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">${this.escapeHtml(tag)}</span>`).join('')}
          </div>
        ` : ''}

        ${buildCategoryBadgesHtml(cp, this.escapeHtml.bind(this))}

        ${buildCodepointFieldsHtml(cp, this.escapeHtml.bind(this))}

        <div class="flex items-center gap-2">
          <code class="flex-1 char-page__mono bg-slate-50 px-3 py-2 rounded-lg">${this.escapeHtml(charFormula)}</code>
          <button type="button" class="shrink-0 rounded-lg bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 active:bg-emerald-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50" data-formula="${this.escapeHtml(charFormula)}" data-action="click->characters#copyFromEvent" aria-label="Copy formula to clipboard" title="Includes the = sign. Paste into a Google Sheets or Excel cell.">Copy formula</button>
        </div>
      </div>
    `

    this.renderSimilarSection(cp)
    this.renderSheetArtMatch(cp)
  }

  findMatchingSheetArtExample(codepoint) {
    const examples = this.sheetArtExamplesValue || []
    return examples.find((example) => example.codepoints?.includes(codepoint)) || null
  }

  renderSheetArtMatch(codepoint) {
    if (!this.hasSheetArtMatchTarget) return

    const example = this.findMatchingSheetArtExample(codepoint)
    if (!example) {
      this.sheetArtMatchTarget.replaceChildren()
      return
    }

    const wrapper = document.createElement('div')
    wrapper.className = 'mt-6 pt-6 border-t border-slate-200'

    const heading = document.createElement('h3')
    heading.className = 'char-page__aside-title mb-3'
    heading.textContent = 'Playground formula using this character'
    wrapper.appendChild(heading)

    const card = document.createElement('div')
    card.className = 'rounded-lg bg-slate-50 p-3 space-y-2'

    const preview = document.createElement('p')
    preview.className = 'char-glyph font-mono text-base truncate'
    preview.title = example.preview
    preview.textContent = example.preview
    card.appendChild(preview)

    const title = document.createElement('p')
    title.className = 'char-page__card-title'
    title.textContent = example.title
    card.appendChild(title)

    const actions = document.createElement('div')
    actions.className = 'flex flex-wrap items-center gap-3'

    const link = document.createElement('a')
    link.href = '/char/playground'
    link.className = 'text-sm text-emerald-700 hover:text-emerald-900'
    link.textContent = 'View in Playground'
    actions.appendChild(link)

    const copyButton = document.createElement('button')
    copyButton.type = 'button'
    copyButton.className =
      'text-sm font-semibold text-emerald-700 hover:text-emerald-900'
    copyButton.dataset.formula = example.formula
    copyButton.dataset.action = 'click->characters#copyFromEvent'
    copyButton.setAttribute('aria-label', 'Copy example formula to clipboard')
    copyButton.title = 'Includes the = sign. Paste into a Google Sheets cell.'
    copyButton.textContent = 'Copy formula'
    actions.appendChild(copyButton)

    card.appendChild(actions)
    wrapper.appendChild(card)
    this.sheetArtMatchTarget.replaceChildren(wrapper)
  }

  renderSimilarSection(codepoint) {
    if (!this.hasSimilarSectionTarget) return

    const related = getRelatedCodepoints(codepoint)
    if (!related.length) {
      this.similarSectionTarget.replaceChildren()
      return
    }

    const wrapper = document.createElement('div')
    wrapper.className = 'mt-6'

    const heading = document.createElement('h3')
    heading.className = 'char-page__aside-title mb-3'
    heading.textContent = 'Similar characters'
    wrapper.appendChild(heading)

    const grid = document.createElement('div')
    grid.className = 'flex flex-wrap gap-2'
    grid.setAttribute('role', 'list')
    grid.setAttribute('aria-label', 'Related character glyphs')

    related.forEach((cp) => grid.appendChild(this.createGlyphButton(cp)))

    wrapper.appendChild(grid)
    this.similarSectionTarget.replaceChildren(wrapper)
  }

  createGlyphButton(codepoint) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.dataset.codepoint = String(codepoint)
    btn.dataset.action = 'click->characters#selectGlyphFromEvent'
    btn.title = this.glyphLabel(codepoint)
    btn.setAttribute('aria-label', `${this.glyphLabel(codepoint)}, ${this.charFormula(codepoint)}`)
    btn.className =
      'char-glyph char-glyph-cell char-glyph-similar flex items-center justify-center rounded-lg bg-transparent border-0 cursor-pointer transition-[transform,box-shadow,background-color] duration-150 ease-out hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50'

    const span = document.createElement('span')
    span.className = 'char-glyph pointer-events-none select-none leading-none'
    span.textContent = this.glyphFor(codepoint)
    btn.appendChild(span)

    return btn
  }

  pickRandomTen() {
    if (!this.hasRandomTenTarget) return

    const pool = codepointsForBlock({ id: 'all' }).filter((cp) => cp > 0x007e)
    const picked = this.shuffle([...pool]).slice(0, 10)

    const list = document.createElement('div')
    list.className = 'flex flex-wrap gap-2'

    picked.forEach((cp) => list.appendChild(this.createGlyphButton(cp)))

    this.randomTenTarget.replaceChildren(list)
  }

  highlightSelectedCell() {
    const toggleSelected = (cell) => {
      const cp = parseInt(cell.dataset.codepoint, 10)
      const selected = this.selectedCp === cp
      cell.classList.toggle('is-selected', selected)
      cell.classList.toggle('-translate-y-px', selected)
      cell.classList.toggle('bg-emerald-500/[0.06]', selected)

      if (cell.tagName === 'BUTTON') {
        cell.setAttribute('aria-pressed', String(selected))
      } else if (selected) {
        cell.setAttribute('aria-current', 'true')
      } else {
        cell.removeAttribute('aria-current')
      }
    }

    if (this.hasGridTarget) {
      this.gridTarget.querySelectorAll('.char-glyph-cell').forEach(toggleSelected)
    }

    this.element.querySelectorAll('#char-popular-characters [data-codepoint]').forEach(toggleSelected)
  }

  scrollToSelectedCell() {
    if (!this.selectedCp || !this.hasGridTarget) return
    if (!isCuratedCodepoint(this.selectedCp)) return

    const cell = this.gridTarget.querySelector(`[data-codepoint="${this.selectedCp}"]`)
    if (!cell) return

    cell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }

  scrollToGridTop() {
    if (this.hasGridTarget) {
      this.gridTarget.scrollTop = 0
    }

    const gridSection = this.element.querySelector('#char-glyph-grid')
    if (!gridSection) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const toolbar = this.element.querySelector('#char-toolbar')
    const toolbarHeight = toolbar ? Math.ceil(toolbar.getBoundingClientRect().height) : 0
    const targetTop = window.scrollY + gridSection.getBoundingClientRect().top - toolbarHeight

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    })
  }

  applyTextColor(hex, { persist = true } = {}) {
    const normalized = normalizeHex(hex) || DEFAULT_TEXT_COLOR
    this.textColor = normalized
    this.element.style.setProperty('--char-color', normalized)
    if (this.hasTextColorBarTarget) this.textColorBarTarget.style.backgroundColor = normalized
    if (this.hasTextColorCustomTarget) this.textColorCustomTarget.value = normalized
    if (persist) localStorage.setItem(TEXT_COLOR_STORAGE_KEY, normalized)
    this.updateTextColorSelection()
  }

  closeTextColorPopover() {
    this.textColorOpen = false
    this.customTextColorsEditing = false
    this.togglePopover(this.textColorPopoverTarget, false)
  }

  removeCustomTextColor(hex) {
    this.customTextColors = saveCustomTextColors(this.customTextColors.filter((c) => c !== hex))
    if (this.textColor === hex) this.applyTextColor(DEFAULT_TEXT_COLOR)
    this.renderCustomTextColors()
    this.updateTextColorSelection()
  }

  renderTextColorPicker() {
    if (!this.hasTextColorPaletteTarget) return

    const palette = this.textColorPaletteTarget
    palette.replaceChildren()

    palette.appendChild(this.buildTextColorResetButton())
    palette.appendChild(this.buildTextColorThemeGrid())
    palette.appendChild(this.buildTextColorStandardSection())
    palette.appendChild(this.buildTextColorCustomSection())

    this.updateTextColorSelection()
  }

  buildTextColorResetButton() {
    const button = document.createElement('button')
    button.type = 'button'
    button.className =
      'flex items-center justify-center w-full h-9 rounded-lg hover:bg-slate-50 transition-colors mb-2'
    button.dataset.action = 'click->characters#resetTextColor'
    button.setAttribute('aria-label', 'Reset text color to black')
    button.title = 'Reset'
    button.innerHTML = `
      <svg class="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3s-5.5 6.8-5.5 10.5a5.5 5.5 0 1 0 11 0C17.5 9.8 12 3 12 3z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/>
        <path d="M5 19 19 5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
      </svg>
    `
    return button
  }

  buildTextColorThemeGrid() {
    const grid = document.createElement('div')
    grid.className = 'grid grid-cols-10 gap-1 mb-3'
    grid.setAttribute('role', 'listbox')
    grid.setAttribute('aria-label', 'Theme colors')

    SHEETS_THEME_GRID.forEach((row, rowIndex) => {
      row.forEach((hex, colIndex) => {
        const label = SHEETS_THEME_LABELS[rowIndex]?.[colIndex] || hex
        grid.appendChild(this.buildTextColorSwatch(hex, label, 'theme'))
      })
    })

    return grid
  }

  buildTextColorStandardSection() {
    const section = document.createElement('div')
    section.className = 'mb-3'

    const heading = document.createElement('p')
    heading.className = 'char-page__meta-label mb-1.5'
    heading.textContent = 'Standard'
    section.appendChild(heading)

    const row = document.createElement('div')
    row.className = 'grid grid-cols-8 gap-1.5'
    row.setAttribute('role', 'listbox')
    row.setAttribute('aria-label', 'Standard colors')

    SHEETS_STANDARD_COLORS.forEach(({ label, value }) => {
      row.appendChild(this.buildTextColorSwatch(value, label, 'standard'))
    })

    section.appendChild(row)
    return section
  }

  buildTextColorCustomSection() {
    const section = document.createElement('div')
    section.dataset.charactersTextColorCustomSection = 'true'

    const header = document.createElement('div')
    header.className = 'flex items-center justify-between mb-1.5'

    const heading = document.createElement('p')
    heading.className = 'char-page__meta-label'
    heading.textContent = 'Custom'
    header.appendChild(heading)

    const actions = document.createElement('div')
    actions.className = 'flex items-center gap-1'

    const addButton = document.createElement('button')
    addButton.type = 'button'
    addButton.className =
      'inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-slate-50 text-slate-600 transition-colors disabled:opacity-40 disabled:pointer-events-none'
    addButton.dataset.action = 'click->characters#openCustomTextColorPicker'
    addButton.setAttribute('aria-label', 'Add custom color')
    addButton.title = 'Add custom color'
    addButton.innerHTML = `
      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `
    actions.appendChild(addButton)

    const editButton = document.createElement('button')
    editButton.type = 'button'
    editButton.className =
      'inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-slate-50 text-slate-600 transition-colors'
    editButton.dataset.action = 'click->characters#toggleCustomTextColorEdit'
    editButton.dataset.charactersTextColorEditButton = 'true'
    editButton.setAttribute('aria-label', 'Edit custom colors')
    editButton.setAttribute('aria-pressed', 'false')
    editButton.title = 'Edit custom colors'
    editButton.innerHTML = `
      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/>
        <path d="m13.5 6.5 3 3" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
      </svg>
    `
    actions.appendChild(editButton)

    header.appendChild(actions)
    section.appendChild(header)

    const row = document.createElement('div')
    row.className = 'flex flex-wrap gap-1.5 min-h-[1.75rem]'
    row.dataset.charactersTextColorCustomRow = 'true'
    row.setAttribute('role', 'listbox')
    row.setAttribute('aria-label', 'Custom colors')
    section.appendChild(row)

    this.renderCustomTextColors(section)
    return section
  }

  renderCustomTextColors(section = null) {
    const container =
      section?.querySelector('[data-characters-text-color-custom-row]') ||
      this.textColorPaletteTarget?.querySelector('[data-characters-text-color-custom-row]')
    if (!container) return

    container.replaceChildren()

    const addButton = this.textColorPaletteTarget?.querySelector(
      '[data-characters-text-color-custom-section] [data-action*="openCustomTextColorPicker"]',
    )
    if (addButton) {
      addButton.disabled = this.customTextColors.length >= MAX_CUSTOM_TEXT_COLORS
      addButton.setAttribute(
        'aria-label',
        this.customTextColors.length >= MAX_CUSTOM_TEXT_COLORS
          ? 'Custom color limit reached'
          : 'Add custom color',
      )
    }

    const editButton = this.textColorPaletteTarget?.querySelector('[data-characters-text-color-edit-button]')
    if (editButton) {
      editButton.classList.toggle('bg-slate-100', this.customTextColorsEditing)
      editButton.setAttribute('aria-pressed', String(this.customTextColorsEditing))
      editButton.setAttribute(
        'aria-label',
        this.customTextColorsEditing ? 'Done editing custom colors' : 'Edit custom colors',
      )
    }

    if (!this.customTextColors.length) {
      const empty = document.createElement('p')
      empty.className = 'text-xs text-slate-400 py-0.5'
      empty.textContent = this.customTextColorsEditing ? 'No custom colors to edit' : 'Add a color with +'
      container.appendChild(empty)
      return
    }

    this.customTextColors.forEach((hex) => {
      container.appendChild(this.buildTextColorSwatch(hex, `Custom ${hex}`, 'custom', { custom: true }))
    })
  }

  buildTextColorSwatch(hex, label, size = 'theme', { custom = false } = {}) {
    const normalized = normalizeHex(hex) || DEFAULT_TEXT_COLOR
    const button = document.createElement('button')
    button.type = 'button'
    button.dataset.color = normalized
    button.dataset.charactersTextColorSwatch = 'true'
    if (custom) button.dataset.customColor = 'true'
    button.dataset.action = 'click->characters#pickTextColor'
    button.setAttribute('role', 'option')
    button.setAttribute('aria-label', label)
    button.setAttribute('aria-selected', 'false')
    button.title = label

    const sizeClass =
      size === 'standard'
        ? 'w-7 h-7'
        : size === 'custom'
          ? 'w-7 h-7'
          : 'w-[1.35rem] h-[1.35rem]'

    button.className = [
      'relative rounded-full ring-1 ring-slate-200/80 hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50',
      sizeClass,
      custom && this.customTextColorsEditing ? 'ring-2 ring-red-300' : '',
    ]
      .filter(Boolean)
      .join(' ')
    button.style.backgroundColor = normalized

    const check = document.createElement('span')
    check.dataset.charactersTextColorCheck = 'true'
    check.className = 'pointer-events-none absolute inset-0 flex items-center justify-center opacity-0'
    check.setAttribute('aria-hidden', 'true')
    check.innerHTML = this.textColorCheckmarkSvg(isLightColor(normalized))
    button.appendChild(check)

    if (custom && this.customTextColorsEditing) {
      const remove = document.createElement('span')
      remove.className =
        'pointer-events-none absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white'
      remove.textContent = '×'
      remove.setAttribute('aria-hidden', 'true')
      button.appendChild(remove)
    }

    return button
  }

  textColorCheckmarkSvg(lightBackground) {
    const stroke = lightBackground ? '#1e293b' : '#ffffff'
    return `
      <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 13l4 4L19 7" stroke="${stroke}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
  }

  updateTextColorSelection() {
    if (!this.hasTextColorPaletteTarget) return

    const current = normalizeHex(this.textColor) || DEFAULT_TEXT_COLOR
    this.textColorPaletteTarget.querySelectorAll('[data-characters-text-color-swatch]').forEach((swatch) => {
      const swatchHex = normalizeHex(swatch.dataset.color)
      const selected = swatchHex === current
      swatch.setAttribute('aria-selected', String(selected))
      swatch.classList.toggle('ring-2', selected)
      swatch.classList.toggle('ring-emerald-500', selected)
      swatch.classList.toggle('ring-offset-1', selected)

      const check = swatch.querySelector('[data-characters-text-color-check]')
      if (check) check.classList.toggle('opacity-0', !selected)
    })
  }

  async copyFormula(formula, button) {
    const text = formula.startsWith('=') ? formula : `=${formula}`
    const copied = await this.copyTextToClipboard(text, button, { successMessage: 'Formula copied to clipboard' })
    if (!copied) return

    const analyticsHost = this.element.closest('[data-controller~="playground-analytics"]')
    trackFormulaCopy({
      pageType: analyticsHost?.dataset.playgroundAnalyticsPageTypeValue || this.playgroundAnalyticsPageType(),
      hubSlug: analyticsHost?.dataset.playgroundAnalyticsHubSlugValue || undefined,
      formulaTitle: button?.dataset.exampleTitle,
      categories: button?.dataset.exampleCategories,
      source: button?.dataset.copySource,
    })
  }

  playgroundAnalyticsPageType() {
    if (this.element.classList.contains('char-page--playground')) return 'index'
    if (document.getElementById('char-sheet-art')) return 'char_teaser'
    return 'char'
  }

  async copyCharacter(codepoint, button) {
    await this.copyTextToClipboard(this.glyphFor(codepoint), button, { successMessage: 'Character copied to clipboard' })
  }

  async copyTextToClipboard(text, button, { successMessage = 'Copied to clipboard' } = {}) {
    if (button?.dataset.copying === 'true') return false

    if (button) button.dataset.copying = 'true'

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else if (!this.fallbackCopyText(text)) {
        throw new Error('clipboard unavailable')
      }

      this.showToast(successMessage)
      if (button && button.dataset.copyIcon !== 'true' && button.dataset.copyGlyph !== 'true') {
        const original = button.textContent
        button.textContent = 'Copied'
        setTimeout(() => {
          button.textContent = original
        }, 2000)
      }
      return true
    } catch {
      this.showToast('Copy failed. Select the text and press Cmd+C or Ctrl+C.')
      return false
    } finally {
      if (button) delete button.dataset.copying
    }
  }

  fallbackCopyText(text) {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)
      return copied
    } catch {
      return false
    }
  }

  copyIconSvg() {
    return `
      <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.75"/>
        <path d="M5 15V5a2 2 0 0 1 2-2h10" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
  }

  showToast(message) {
    if (!this.hasToastTarget) return
    this.toastTarget.textContent = message
    this.toastTarget.classList.remove('hidden', 'opacity-0')
    clearTimeout(this.toastTimeout)
    this.toastTimeout = setTimeout(() => {
      this.toastTarget.classList.add('opacity-0')
      setTimeout(() => this.toastTarget.classList.add('hidden'), 200)
    }, 2000)
  }

  validCodepoint(cp) {
    return Number.isInteger(cp) && cp >= 0 && cp <= 0x10ffff
  }

  charFormula(codepoint) {
    return `=CHAR(${codepoint})`
  }

  glyphFor(codepoint) {
    if (codepoint === 10) return '↵'
    if (codepoint === 9) return '⇥'
    try {
      return String.fromCodePoint(codepoint)
    } catch {
      return '?'
    }
  }

  glyphLabel(codepoint) {
    return getCharacterMeta(codepoint).name
  }

  loadUnicodeNameIfNeeded(codepoint) {
    if (isCuratedCodepoint(codepoint)) return

    ensureUnicodeNames().then(() => {
      if (this.selectedCp !== codepoint) return
      this.renderDetailPanel()
      this.highlightSelectedCell()
    })
  }

  flashInputError(input) {
    input.classList.add('ring-2', 'ring-red-400')
    input.classList.add('animate-[shake_0.3s_ease-in-out]')
    setTimeout(() => input.classList.remove('animate-[shake_0.3s_ease-in-out]'), 300)
  }

  clearInputError(input) {
    input.classList.remove('ring-2', 'ring-red-400')
  }

  togglePopover(element, open) {
    if (!element) return
    element.classList.toggle('hidden', !open)
  }

  shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}

